import { useFormik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { DefaultContext } from '../../../contexts/default';
import masks from '../../../helpers/utils/masks';
import PreAlert from '../../../helpers/utils/preAlert';
import api from '../../../services/api';
import Button from '../../bootstrap/Button';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import Spinner from '../../bootstrap/Spinner';
import FormGroup from '../../bootstrap/forms/FormGroup';
import Input from '../../bootstrap/forms/Input';

const validate = values => {
  const errors = {};
  if (values.name.trim().length < 3) errors.name = 'Informe o nome completo';
  if (!/^\d{11}$/.test(values.cpf.replace(/\D/g, ''))) errors.cpf = 'Informe o CPF completo';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Informe um e-mail válido';
  const phone = values.phone.replace(/\D/g, '');
  if (phone.length < 10 || phone.length > 11) errors.phone = 'Informe um telefone válido';
  if (!values.birthDate.trim()) errors.birthDate = 'Informe a data de nascimento';
  if (!values.idCard.trim()) errors.idCard = 'Informe o NFC';
  return errors;
};

const errorMessage = error => error?.response?.data?.message || 'Não foi possível salvar o beneficiário';

const ModalRegister = ({ open, setIsOpen, beneficiary, establishmentId, onSaved }) => {
  const { onShowAlert } = useContext(DefaultContext);
  const [loading, setLoading] = useState(false);
  const editing = Boolean(beneficiary);

  const formik = useFormik({
    initialValues: {
      name: '',
      cpf: '',
      email: '',
      phone: '',
      birthDate: '',
      idCard: '',
    },
    validate,
    onSubmit: async values => {
      setLoading(true);
      const body = {
        establishmentId,
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.replace(/\D/g, ''),
        birthDate: values.birthDate.trim(),
      };

      if (!editing) {
        body.cpf = values.cpf.replace(/\D/g, '');
        body.idCard = values.idCard.trim().toUpperCase();
      } else if (beneficiary.cardStatus === 'pending_activation') {
        body.idCard = values.idCard.trim().toUpperCase();
      }

      try {
        if (editing) {
          await api.put(`routes/beneficiaries/${beneficiary.beneficiaryId}`, body);
        } else {
          await api.post('routes/beneficiaries', body);
        }
        onShowAlert(PreAlert.success(`Beneficiário ${editing ? 'atualizado' : 'cadastrado'} com sucesso`));
        setIsOpen(false);
        onSaved();
      } catch (error) {
        onShowAlert(PreAlert.error(errorMessage(error)));
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    formik.setValues({
      name: beneficiary?.name || '',
      cpf: beneficiary?.cpf || '',
      email: beneficiary?.email || '',
      phone: beneficiary?.phone || '',
      birthDate: beneficiary?.birthDate || '',
      idCard: beneficiary?.idCard || '',
    });
  }, [beneficiary, open]);

  const fieldProps = name => ({
    id: name,
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    isValid: formik.isValid,
    isTouched: formik.touched[name],
    invalidFeedback: formik.errors[name],
  });

  return (
    <Modal
      id='modal-beneficiary'
      titleId='beneficiary-title'
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop
      isCentered
      size='lg'>
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle id='beneficiary-title'>
          {editing ? 'Editar beneficiário' : 'Cadastrar beneficiário'}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className='p-5'>
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className='row'>
            <FormGroup id='cpf-group' label='CPF' className='col-md-4 mb-4'>
              <Input {...fieldProps('cpf')} value={masks.cpf(formik.values.cpf)} disabled={editing} />
            </FormGroup>
            <FormGroup id='name-group' label='Nome' className='col-md-8 mb-4'>
              <Input {...fieldProps('name')} value={formik.values.name} />
            </FormGroup>
            <FormGroup id='birthDate-group' label='Data de nascimento' className='col-md-4 mb-4'>
              <Input {...fieldProps('birthDate')} value={masks.date(formik.values.birthDate)} placeholder='DD/MM/AAAA' />
            </FormGroup>
            <FormGroup id='phone-group' label='Telefone' className='col-md-4 mb-4'>
              <Input {...fieldProps('phone')} value={masks.phone(formik.values.phone)} />
            </FormGroup>
            <FormGroup id='idCard-group' label='NFC' className='col-md-4 mb-4'>
              <Input
                {...fieldProps('idCard')}
                value={formik.values.idCard}
                disabled={editing && beneficiary?.cardStatus !== 'pending_activation'}
              />
            </FormGroup>
            <FormGroup id='email-group' label='E-mail' className='col-md-12 mb-4'>
              <Input {...fieldProps('email')} type='email' value={formik.values.email} />
            </FormGroup>
          </div>
          <div className='d-flex justify-content-end gap-2 pt-3'>
            <Button color='danger' icon='cancel' onClick={() => setIsOpen(false)}>Cancelar</Button>
            <Button
              type='submit'
              color='success'
              icon='check'
              isDisable={!formik.isValid || loading}>
              {loading && <Spinner color='light' inButton isGrow isSmall size={10} tag='span' />}
              {loading ? ' Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default ModalRegister;
